import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  BarChart3, 
  TrendingUp, 
  Shield, 
  Zap, 
  MessageCircle,
  ArrowRight,
  Sparkles,
  Calculator,
  FileText,
  Users,
  Clock,
  Search,
  Database
} from 'lucide-react';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Brain className="feature-icon" />,
      title: "Tally Prime AI Agent",
      description: "This is an AI Agent which works for Tally Prime account software, providing intelligent assistance for all your accounting needs."
    },
    {
      icon: <BarChart3 className="feature-icon" />,
      title: "Live Data Fetching",
      description: "It fetches all the user required data when user ask on the chat bot. It fetches live data using AI model and transform in user friendly format."
    },
    {
      icon: <Search className="feature-icon" />,
      title: "Comprehensive Search",
      description: "User can search and work with ledgers, customers, clients, sales, purchase vouchers with intelligent AI assistance."
    },
    {
      icon: <Shield className="feature-icon" />,
      title: "Voucher Verification",
      description: "User can verify vouchers, income, expenses and other accounts related information with automated validation."
    },
    {
      icon: <Zap className="feature-icon" />,
      title: "Real-time Processing",
      description: "Instant data processing and updates for real-time financial management with Tally Prime integration."
    },
    {
      icon: <Calculator className="feature-icon" />,
      title: "Smart Calculations",
      description: "Automate complex financial calculations and reduce manual errors with AI-powered accuracy."
    }
  ];

  const stats = [
    { icon: <Database />, value: "100%", label: "Tally Prime Compatible" },
    { icon: <Zap />, value: "< 2s", label: "Response Time" },
    { icon: <Shield />, value: "100%", label: "Data Security" },
    { icon: <Sparkles />, value: "24/7", label: "AI Support" }
  ];

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo">
              <Sparkles className="logo-icon" />
            </div>
                         <div className="brand">
               <h1 className="brand-title">Spark Prime</h1>
               <p className="brand-subtitle">Smart AI for Tally Prime</p>
             </div>
          </div>
          <nav className="nav-menu">
            <button 
              className="ai-chat-btn"
              onClick={() => navigate('/ai-chat')}
            >
              <MessageCircle className="btn-icon" />
              AI Chat
              <ArrowRight className="btn-arrow" />
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h2 className="hero-title">
              Your <span className="highlight">Tally Prime</span> AI Assistant
            </h2>
            <p className="hero-description">
              Experience the future of Tally Prime with our intelligent AI agent. 
              Get instant answers about ledgers, vouchers, customers, and all your accounting data through natural conversation.
            </p>
            <div className="hero-actions">
              <button 
                className="primary-btn"
                onClick={() => navigate('/ai-chat')}
              >
                <MessageCircle className="btn-icon" />
                Start AI Chat
              </button>
              <button className="secondary-btn">
                Learn More
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="floating-card card-1">
              <Brain className="card-icon" />
              <span>Tally AI</span>
            </div>
            <div className="floating-card card-2">
              <Database className="card-icon" />
              <span>Live Data</span>
            </div>
            <div className="floating-card card-3">
              <Search className="card-icon" />
              <span>Smart Search</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-content">
                <h3 className="stat-value">{stat.value}</h3>
                <p className="stat-label">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header">
          <h2 className="section-title">Tally Prime AI Features</h2>
          <p className="section-subtitle">
            Discover how our intelligent AI agent revolutionizes your Tally Prime experience
          </p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon-wrapper">
                {feature.icon}
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

             {/* CTA Section */}
       <section className="cta-section">
         <div className="cta-content">
           <h2 className="cta-title">Ready to Get Started?</h2>
                      <p className="cta-description">
              Be among the first to experience the future of Tally Prime with AI-powered assistance. Start your journey today!
            </p>
           <button 
             className="cta-btn"
             onClick={() => navigate('/ai-chat')}
           >
             <MessageCircle className="btn-icon" />
             Try AI Chat Now
             <ArrowRight className="btn-arrow" />
           </button>
         </div>
       </section>

       {/* Footer */}
       <footer className="footer">
         <div className="footer-content">
           <div className="footer-section">
             <h3 className="footer-title">iBirds Software Services Pvt Ltd</h3>
             <div className="contact-info">
               <p className="contact-item">
                 <a href="mailto:contact@ibirdsservices.com" className="contact-link">
                   contact@ibirdsservices.com
                 </a>
               </p>
               <p className="contact-item">
                 <a href="tel:+919251425422" className="contact-link">
                   +91-9251425422
                 </a>
               </p>
               <p className="contact-item">
                 <a href="https://ibirdsservices.com" target="_blank" rel="noopener noreferrer" className="contact-link">
                   https://ibirdsservices.com
                 </a>
               </p>
             </div>
           </div>
           <div className="footer-bottom">
             <p className="copyright">
               © 2024 iBirds Software Services Pvt Ltd. All rights reserved.
             </p>
           </div>
         </div>
       </footer>
     </div>
   );
 };

export default Dashboard;
